import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /** @type {number | null} */
  committedQuantity = null;

  connectedCallback() {
    super.connectedCallback();
    this.committedQuantity = this.getCurrentValues().value;
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton, quantityInput } = this.refs;
    if (quantityInput.disabled) {
      minusButton.disabled = true;
      plusButton.disabled = true;
      return;
    }

    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Commits direct keyboard entry without issuing a second cart request when
   * the browser fires both change and blur for the same edit.
   * @param {Event} event
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const nextQuantity = Number.parseInt(event.target.value, 10);
    if (Number.isFinite(nextQuantity) && nextQuantity === this.committedQuantity) {
      this.updateButtonStates();
      return;
    }

    super.setQuantity(event);
  }

  /**
   * Enter should commit a typed cart quantity immediately rather than waiting
   * for the shopper to click elsewhere.
   * @param {KeyboardEvent} event
   */
  setQuantityOnEnter(event) {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    event.target.blur();
  }

  onQuantityChange() {
    this.committedQuantity = this.getCurrentValues().value;
    super.onQuantityChange();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
